/** How many branding setups one person can keep for PropertyCuts videos. */
export const MAX_BRAND_KITS = 20;

export const BRAND_KIT_NAME_MAX = 60;

export interface BrandKitChoice {
  id: string;
  isDefault: boolean;
}

/** Collapse spaces and reject a blank or oversized name. */
export function cleanBrandKitName(name: string): string {
  const cleaned = name.trim().replace(/\s+/g, ' ');
  if (!cleaned) throw new Error('Give this branding a name.');
  if (cleaned.length > BRAND_KIT_NAME_MAX) {
    throw new Error('Keep the name under 60 characters.');
  }
  return cleaned;
}

/**
 * Which saved branding a new video should start with.
 * A chosen id is used only when that setup is still in the list.
 * No choice uses the default, then the first setup.
 * An explicit empty choice starts with no saved branding.
 */
export function pickBrandKitId(
  kits: BrandKitChoice[],
  kitId?: string | null,
): string | null {
  if (kitId === null) return null;
  if (typeof kitId === 'string') {
    return kits.some((kit) => kit.id === kitId) ? kitId : null;
  }
  return kits.find((kit) => kit.isDefault)?.id ?? kits[0]?.id ?? null;
}

/** Turn a unique-index failure into a sentence for the branding form. */
export function brandKitWriteError(
  error: { code?: string; message?: string } | null,
  fallback: string,
): Error {
  const message = error?.message ?? '';
  if (error?.code === '23505' && message.includes('propertycut_brand_kits_one_default')) {
    return new Error('Could not set the default branding. Try again.');
  }
  if (error?.code === '23505' || message.toLowerCase().includes('duplicate')) {
    return new Error('You already have branding with that name.');
  }
  return new Error(message || fallback);
}
